import { useEffect, useState, type FormEvent } from "react";
import {
  Clock,
  FileText,
  MapPin,
  Play,
  Square,
  TriangleAlert,
} from "lucide-react";
import { ApiError, downloadFile } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  priorityStyle,
  statusTone,
} from "@/features/tickets/labels";
import { slaBadge } from "@/features/tickets/sla";
import {
  currentPosition,
  elapsedSeconds,
  formatDuration,
  serviceReportPdfUrl,
  type WorkType,
} from "./api";
import {
  useCheckIn,
  useCheckOut,
  useCreateServiceReport,
  useServiceReport,
  useTodaysJobs,
} from "./hooks";

const WORK_TYPES: { value: WorkType; label: string }[] = [
  { value: "onsite", label: "On site" },
  { value: "remote", label: "Remote" },
  { value: "travel", label: "Travel" },
  { value: "diagnosis", label: "Diagnosis" },
  { value: "other", label: "Other" },
];

export default function EngineerPage() {
  const { data, isLoading, isError, error } = useTodaysJobs();
  const checkIn = useCheckIn();
  const checkOut = useCheckOut();

  const [actionError, setActionError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [reportTicketId, setReportTicketId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Drives the live elapsed timer on an open check-in.
  useEffect(() => {
    if (!data?.open_time_log) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [data?.open_time_log]);

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  async function handleCheckIn(ticketId: string, workType: WorkType) {
    setLocating(true);
    // Never blocks: a denied or unavailable fix resolves to null and the
    // server records the check-in as unverified.
    const position = await currentPosition();
    setLocating(false);
    await run(() =>
      checkIn.mutateAsync({
        ticketId,
        body: {
          latitude: position?.latitude ?? null,
          longitude: position?.longitude ?? null,
          work_type: workType,
        },
      }),
    );
  }

  async function handleCheckOut() {
    setLocating(true);
    const position = await currentPosition();
    setLocating(false);
    await run(() =>
      checkOut.mutateAsync({
        latitude: position?.latitude ?? null,
        longitude: position?.longitude ?? null,
      }),
    );
  }

  const openLog = data?.open_time_log ?? null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My jobs</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Assigned work, most urgent first.
        </p>
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}

      {openLog && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
              Checked in
              {!openLog.checkin_verified && (
                <span className="inline-flex items-center gap-1 text-xs text-sla-warn">
                  <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  Location unverified
                </span>
              )}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid="elapsed">
              {formatDuration(elapsedSeconds(openLog.started_at))}
            </div>
            <div className="text-xs text-muted-foreground">
              since {formatDateTime(openLog.started_at)}
              {openLog.checkin_distance_m !== null &&
                ` · ${Math.round(openLog.checkin_distance_m)} m from site`}
            </div>
          </div>
          <Button
            variant="destructive"
            disabled={checkOut.isPending || locating}
            onClick={() => void handleCheckOut()}
          >
            <Square className="h-4 w-4" aria-hidden="true" />
            {locating ? "Locating…" : "Check out"}
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Open jobs" value={String(data?.jobs.length ?? 0)} />
        <Stat
          label="Logged (30 days)"
          value={formatDuration((data?.minutes_logged_30d ?? 0) * 60)}
        />
        <Stat label="Visits (30 days)" value={String(data?.visits_30d ?? 0)} />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : "Could not load your jobs."}
        </p>
      )}
      {data?.jobs.length === 0 && (
        <p className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          Nothing assigned to you right now.
        </p>
      )}

      <ul className="space-y-3">
        {data?.jobs.map((job) => {
          const sla = slaBadge(job.sla.resolution_state, job.sla.resolution_due_at);
          const isCurrent = openLog?.ticket_id === job.id;
          return (
            <li key={job.id} className="rounded-lg border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      style={priorityStyle(job.priority)}
                    >
                      {PRIORITY_LABELS[job.priority]}
                    </span>
                    <Badge tone={statusTone(job.status)}>{STATUS_LABELS[job.status]}</Badge>
                    <Badge tone={sla.tone}>{sla.label}</Badge>
                  </div>
                  <div className="mt-1.5 font-medium">{job.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {job.ticket_number} · {job.customer_name}
                    {job.location_name && (
                      <>
                        {" · "}
                        <MapPin className="inline h-3 w-3" aria-hidden="true" />{" "}
                        {job.location_name}
                      </>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <JobActions
                    disabled={checkIn.isPending || locating || openLog !== null}
                    isCurrent={isCurrent}
                    locating={locating}
                    onCheckIn={(workType) => void handleCheckIn(job.id, workType)}
                  />
                  <Button variant="outline" onClick={() => setReportTicketId(job.id)}>
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    Report
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <ServiceReportDialog
        ticketId={reportTicketId}
        onClose={() => setReportTicketId(null)}
      />
      <span className="sr-only" aria-hidden="true">
        {tick}
      </span>
    </div>
  );
}

function JobActions({
  disabled,
  isCurrent,
  locating,
  onCheckIn,
}: {
  disabled: boolean;
  isCurrent: boolean;
  locating: boolean;
  onCheckIn: (workType: WorkType) => void;
}) {
  const [workType, setWorkType] = useState<WorkType>("onsite");

  if (isCurrent) {
    return <Badge tone="success">In progress</Badge>;
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        aria-label="Work type"
        className="w-auto min-w-[120px]"
        value={workType}
        onChange={(e) => setWorkType(e.target.value as WorkType)}
      >
        {WORK_TYPES.map((w) => (
          <option key={w.value} value={w.value}>
            {w.label}
          </option>
        ))}
      </Select>
      <Button disabled={disabled} onClick={() => onCheckIn(workType)}>
        <Play className="h-4 w-4" aria-hidden="true" />
        {locating ? "Locating…" : "Check in"}
      </Button>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-card-foreground">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ServiceReportDialog({
  ticketId,
  onClose,
}: {
  ticketId: string | null;
  onClose: () => void;
}) {
  const { data: report, isLoading } = useServiceReport(ticketId);
  const create = useCreateServiceReport();
  const [summary, setSummary] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [partName, setPartName] = useState("");
  const [partQty, setPartQty] = useState("1");
  const [parts, setParts] = useState<{ name: string; quantity: number }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ticketId === null) {
      setSummary("");
      setRecommendations("");
      setParts([]);
      setError(null);
    }
  }, [ticketId]);

  async function runInDialog(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!ticketId) return;
    try {
      await create.mutateAsync({
        ticketId,
        body: {
          work_summary: summary.trim(),
          parts_used: parts,
          recommendations: recommendations.trim() || null,
        },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <Dialog open={ticketId !== null} onClose={onClose} title="Service report">
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {report && (
        <div className="space-y-3 text-sm">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
            >
              {error}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{report.report_number}</span>
            <Badge tone={report.is_signed ? "success" : "warning"}>
              {report.is_signed ? `Signed by ${report.signed_by_name}` : "Awaiting signature"}
            </Badge>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Work carried out</div>
            <p className="whitespace-pre-wrap">{report.work_summary}</p>
          </div>
          <div className="text-xs text-muted-foreground">
            Time on site: {formatDuration(report.total_minutes * 60)}
          </div>
          {/* A plain <a download> 401s: the bearer token lives in memory,
              so the browser cannot attach it. downloadFile fetches with auth
              and hands the blob to the browser. */}
          <Button
            variant="outline"
            onClick={() =>
              void runInDialog(() =>
                downloadFile(
                  serviceReportPdfUrl(report.id),
                  `${report.report_number}.pdf`,
                ),
              )
            }
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Download PDF
          </Button>
        </div>
      )}

      {!isLoading && !report && (
        <form onSubmit={submit} className="space-y-4" noValidate>
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <Field label="Work carried out" htmlFor="work_summary">
            <textarea
              id="work_summary"
              required
              rows={4}
              minLength={10}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Field>

          <div>
            <div className="mb-1.5 text-sm font-medium">Parts used</div>
            {parts.length > 0 && (
              <ul className="mb-2 space-y-1 text-sm">
                {parts.map((p, i) => (
                  <li key={`${p.name}-${i}`} className="flex items-center justify-between">
                    <span>
                      {p.name} × {p.quantity}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setParts((list) => list.filter((_, j) => j !== i))}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <Input
                aria-label="Part name"
                placeholder="Part"
                value={partName}
                onChange={(e) => setPartName(e.target.value)}
              />
              <Input
                aria-label="Part quantity"
                type="number"
                min={1}
                className="w-20"
                value={partQty}
                onChange={(e) => setPartQty(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                disabled={!partName.trim()}
                onClick={() => {
                  setParts((list) => [
                    ...list,
                    { name: partName.trim(), quantity: Math.max(1, Number(partQty) || 1) },
                  ]);
                  setPartName("");
                  setPartQty("1");
                }}
              >
                Add
              </Button>
            </div>
          </div>

          <Field label="Recommendations (optional)" htmlFor="recommendations">
            <Input
              id="recommendations"
              value={recommendations}
              onChange={(e) => setRecommendations(e.target.value)}
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || summary.trim().length < 10}>
              {create.isPending ? "Saving…" : "Create report"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
